import React, { useEffect, useState } from 'react'
import CustomValidatorHook from '../CustomValidatorHook'
import CustomFieldsHook from '../CustomFieldsHook'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import {
    fillValuesForEdit,
    renderSetUnitInitialState,
    renderSetUnitModal
} from '../../../store/actionsCreators/setUnitActions'
import { useNavigate, useParams } from 'react-router'
import { type AppDispatch, type RootState, store } from '../../../store/store'
import { addUnit, archiveUnit, updateUnit } from '../../../store/actionsCreators/unitsActions'
import useUserType from '../../../hooks/UseUserType'
import {
    dropModalSlice,
    setDocuments,
    setDynamicDropDown,
    setImages
} from '../../../store/slices/modalSlice'

import { dropSetUnitSlice } from '../../../store/slices/setUnitSlice'
import CustomModalHeader from '../CustomModalHeader'
import { swalAttentionLeave } from '../../../functions/swalOptions'
import swalConfig from '../../texts/swalTexts'

import mapPropertiesForSelect from '../../../functions/shared/properties/mapPropertiesForSelect'
import useMediaQuery from '../../../hooks/useMediaQuery'

export interface UnitCreationPresentation {
    header: string
    notice: string
    submitLabel: string
}

interface SetUnitModalProps {
    sourceUnitId?: number
    presentation?: UnitCreationPresentation
    initializeSource?: (unitId: number) => any
    afterCreate?: (newUnitId: number) => Promise<{ isSuccessful: boolean }>
}

const SetUnitModal = ({ sourceUnitId, presentation, initializeSource, afterCreate }: SetUnitModalProps = {}): JSX.Element => {
    const { userType, basicUserType } = useUserType()
    const { modalID, unitID, objectID } = useParams()
    const formKey = useAppSelector((state) => state.setUnit.formKey)

    const { properties } = useAppSelector((state: RootState) => state.property)
    const dispatch = useAppDispatch()
    const [modal, setModal] = useState([dispatch(renderSetUnitModal(modalID ?? 'set_unit_modal'))])
    const isAdminUnitForm = basicUserType === 'AdminBSO'

    const initialState = dispatch(renderSetUnitInitialState())
    const navigate = useNavigate()
    const sourceId = sourceUnitId ?? (unitID != null ? Number(unitID) : null)

    useEffect(() => {
        if (sourceId == null || !Number.isFinite(sourceId)) return
        void dispatch(initializeSource?.(sourceId) ?? fillValuesForEdit(sourceId))
    }, [sourceId, initializeSource])

    if (presentation != null) {
        modal[0].header = `${presentation.header} #${String(sourceId ?? '')} — Step 1 of 2: Unit details`
        modal[0].footer[0].content[0] = presentation.submitLabel
        modal[0].form = [{
            element: 'label-secondary',
            content: [presentation.notice]
        }, ...modal[0].form]
    }

    if (isAdminUnitForm) {
        const unitNumberIndex = modal[0].form.findIndex((row: any) => row.id === 'Number')
        const currentUnit = store.getState().units.units.find((unit) => unit.id === Number(unitID))
        if (unitNumberIndex !== -1 && !modal[0].form.some((row: any) => row.id === 'ERPUnitID')) {
            modal[0].form.splice(unitNumberIndex + 1, 0, {
                id: 'ERPUnitID',
                content: ['ERP Unit ID', 'Enter ERP Unit ID'],
                description: 'External unit identifier from ERP. Enter it exactly as provided.',
                defaultValue: (currentUnit?.attributes as Record<string, any> | undefined)?.ERPUnitID ?? '',
                element: 'input'
            })
        }
        if (
            modalID === 'edit_unit_modal' &&
            currentUnit?.attributes?.ManagedBy === 'BSO' &&
            !modal[0].form.some((row: any) => row.id === 'CataloguePriority')
        ) {
            modal[0].form.splice(unitNumberIndex + 2, 0, {
                id: 'CataloguePriority',
                content: ['Catalogue priority', 'No priority'],
                description: 'BSO positions 1–3. Paid PublishedPlace cards keep their existing placement.',
                selectors: [
                    { label: 'No priority', value: '' },
                    { label: 'Priority 1', value: '1' },
                    { label: 'Priority 2', value: '2' },
                    { label: 'Priority 3', value: '3' }
                ],
                defaultValue: (currentUnit?.attributes as Record<string, any> | undefined)?.CataloguePriority ?? '',
                element: 'select'
            })
        }
    }


    useEffect(() => {
        dispatch(setDynamicDropDown(mapPropertiesForSelect(properties)))
        return () => {
            dispatch(dropModalSlice())
            dispatch(dropSetUnitSlice())
            dispatch(setImages([]))
            dispatch(setDocuments([]))
        }
    }, [])

    useEffect(() => {
        setModal([dispatch(renderSetUnitModal(modalID ?? 'set_unit_modal'))])
    }, [formKey])

    let callback: any
    let handleReset: any = () => { }
    if (modalID === 'edit_unit_modal') {
        const currentUnit = store.getState().units.units.find((unit) => unit?.id === Number(unitID))
        if (currentUnit?.attributes?.Statuses?.includes('Archived') === true) {
            modal[0].footer[0].content[0] = 'Unarchive'
        }

        handleReset = () => {
            dispatch(archiveUnit(+(unitID ?? -1), navigate))
            navigate(-1)
        }
        callback = (data: any) => async (dispatch: AppDispatch, getState: (state: RootState) => RootState) => {
            const response = await dispatch(updateUnit(data, +(unitID ?? unitID ?? -1)))
            /* response.textNavigate = -1
            response.textNotification = '' */
            return response
        }
    } else {
        callback = (data: any) => async (dispatch: AppDispatch, getState: (state: RootState) => RootState): Promise<any> => await new Promise((resolve) => {
            void dispatch(addUnit(data))
                .then(async (response: any) => {
                    if (response.isSuccessful === true) {
                        const newUnitId = Number(response.response.data?.id)
                        if (afterCreate != null && Number.isFinite(newUnitId)) {
                            const historyResult = await afterCreate(newUnitId)
                            if (historyResult.isSuccessful !== true) {
                                resolve({ isSuccessful: false, textNotification: `Listing #${String(newUnitId)} was created, but its relist history could not be saved. Retry the history update before creating another listing.` })
                                return
                            }
                        }
                        resolve({
                            isSuccessful: response.isSuccessful,
                            textNotification: 'Set up marketing for unit',
                            textNavigate: `/${userType.toLowerCase()}/modal/set_unit_marketing_modal/${String(response.response.data?.id)}/-`
                        })
                    } else {
                        resolve({
                            isSuccessful: false,
                            textNotification: response?.textNotification
                        })
                    }
                })
                .catch((err: any) => {
                    console.error(err)
                    resolve({
                        isSuccessful: false
                    })
                })
        })
    }

    const onLeave = (str?: string): void => {
        return void swalAttentionLeave(swalConfig.attentionLeave.messageModalFooterClose).then(async (result: any) => {
            if (result.isConfirmed) {
                str && str !== '' ? navigate(str) : navigate(-1)
            }
        })
    }

    const onCloseForAdvertiseModal = () => {

    }
    const userDevice = useAppSelector((state) => state.app.userDevice)
    const isMobileCanvas = useMediaQuery('(max-width: 480px)'); // или 540

    return (
        <>

            {/* 
   <div className="page d-flex flex-column flex-row-fluid">

            <div style={{ height: "12.5rem" }} />

            <div className={isMobileCanvas ? "" : "w-100 bg-white h-auto d-flex flex-center shadow-sm mw-600px mx-auto card shadow-sm border-0"}>
                <div
                    className="position-fixed top-0 start-0 w-100 d-flex justify-content-center units-fixed-in-content"
                    style={{
                        paddingTop: userDevice === "IPhone" ? "2.5rem" : 0,
                        zIndex: 100,
                    }}
                >

                    <div className="w-100 mw-600px bg-white h-auto fw-bold d-flex flex-column card shadow-sm border-0">
                        <div className="d-flex flex-column p-4 py-0 w-100 position-relative">
                            <UnitsHeaderSh />
                        </div>

                        {!(myRequestedUnitsOpen && userID !== Number(process.env.REACT_APP_BSO_SUPPORT_USER_ID)) && (
                            <>
                                <UnitsNavigateSh filter={{ unitsCategory, filterUse }} onClickCategory={onClickCategory} />
                                <MainUnitFilters />
                            </>
                        )}
                    </div>
                </div>
                <div className="content d-flex flex-column flex-fill pt-4 pb-4 mx-4 overflow-auto   shadow-sm" style={{ height: "100%" }}> */}










            <div className="page d-flex flex-column flex-row-fluid ">
                <div className={isMobileCanvas ? "" : "w-100 bg-white h-auto d-flex flex-center mw-600px mx-auto card border-0"}>
                    <div className="content  w-100   mx-auto">
                        <div className="card d-flex mh-100 ">
                            <div className="card-header" style={{ paddingTop: userDevice === 'IPhone' ? '45px' : '14px', paddingBottom: '14px' }}>
                                <CustomModalHeader modal={modal} onLeave={onLeave} onCloseForAdvertiseModal={onCloseForAdvertiseModal} />
                            </div>

                            <div className="card-body card-scroll h-50  ">
                                <CustomValidatorHook id={modalID ?? ''} fromPage={unitID} unitID={unitID} objectID={objectID} callback={callback} handleReset={handleReset} initialState={initialState}>
                                    <CustomFieldsHook modal={modal} isAuth={false} unitID={unitID} objectID={objectID} />
                                </CustomValidatorHook>
                                <div style={{ height: '120px' }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default SetUnitModal
